/**
 * Analytics route - thin shell.
 *
 * The heavy page component (Recharts) lives in:
 *   src/components/analytics-content.tsx
 *
 * It is lazy-loaded via React.lazy() so recharts (~600 KB) is only
 * downloaded when an admin actually navigates to this route.
 */
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { AnalyticsSkeleton } from "@/components/lazy-analytics";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { validateAnalyticsSearch } from "@/hooks/analytics/use-analytics-filters";

// This dynamic import is the chunk boundary - recharts stays OUT of
// the shared authenticated bundle and only loads on demand.
const AnalyticsContent = lazy(() =>
  import("@/components/analytics-content").then((m) => ({
    default: m.AnalyticsPageContent,
  })),
);

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  // The filter state lives in the URL so a reload or a shared link keeps the selection. This is the
  // whitelist for that state: TanStack Router drops any search param a route does not declare, so a
  // filter key missing here would silently vanish on the next navigation.
  validateSearch: validateAnalyticsSearch,
  component: () => (
    <RequirePermission
      action="view:analytics"
      fallback={<AccessDenied message="You do not have permission to view analytics." />}
    >
      <AnalyticsPage />
    </RequirePermission>
  ),
});

function AnalyticsPage() {
  return (
    <Suspense fallback={<AnalyticsSkeleton />}>
      <AnalyticsContent />
    </Suspense>
  );
}
